import React, { useMemo, useState } from "react";
import { ReadablePillToggle } from "../ui/FormSection";
import BroadcastPanel from "../broadcasts/BroadcastPanel";
import NotifyRow from "./NotifyRow";

const FILTERS = [
  { id: "all", label: "All Contacts" },
  { id: "regulars", label: "Regulars" },
  { id: "past", label: "Past Guests (Not RSVP'd)" },
  { id: "followers", label: "Followers" },
];

function buildContacts({ attendees, updateSubscribers, regulars }) {
  const byId = new Map();

  function upsert(id, patch) {
    if (!id) return;
    const prev = byId.get(id) || {
      dancer_id: id,
      name: "",
      phone: "",
      whatsapp_url: null,
      segments: new Set(),
      last_occurrence: null,
      visit_count: 0,
      on_profile: false,
    };
    if (patch.name) prev.name = patch.name;
    if (patch.phone) prev.phone = patch.phone;
    if (patch.whatsapp_url) prev.whatsapp_url = patch.whatsapp_url;
    if (patch.last_occurrence) prev.last_occurrence = patch.last_occurrence;
    if (patch.visit_count != null && patch.visit_count > prev.visit_count) {
      prev.visit_count = patch.visit_count;
    }
    if (patch.on_profile) prev.on_profile = true;
    for (const seg of patch.segments || []) prev.segments.add(seg);
    byId.set(id, prev);
  }

  for (const a of updateSubscribers || []) {
    upsert(a.dancer_id, {
      name: a.name,
      phone: a.phone,
      whatsapp_url: a.whatsapp_url,
      on_profile: a.on_profile,
      segments: ["followers"],
    });
  }

  for (const a of regulars || []) {
    upsert(a.dancer_id, {
      name: a.name,
      phone: a.phone,
      whatsapp_url: a.whatsapp_url,
      last_occurrence: a.last_occurrence,
      visit_count: a.visit_count,
      segments: ["regulars"],
    });
  }

  for (const a of attendees || []) {
    upsert(a.dancer_id, {
      name: a.name,
      phone: a.phone,
      whatsapp_url: a.whatsapp_url,
      last_occurrence: a.last_occurrence,
      segments: ["past"],
    });
  }

  return [...byId.values()].map((c) => ({
    ...c,
    segments: [...c.segments],
  }));
}

function contactMeta(contact) {
  const bits = [];
  if (contact.on_profile && contact.segments.includes("followers")) {
    bits.push("on profile");
  }
  if (contact.visit_count > 0) {
    bits.push(`${contact.visit_count} visit${contact.visit_count === 1 ? "" : "s"}`);
  }
  if (contact.last_occurrence) {
    bits.push(`last ${contact.last_occurrence}`);
  }
  if (contact.phone) bits.push(contact.phone);
  return bits.join(" · ");
}

function contactStatus(contact) {
  const labels = [];
  if (contact.segments.includes("followers")) labels.push("Follower");
  if (contact.segments.includes("regulars")) labels.push("Regular");
  if (contact.segments.includes("past")) labels.push("Past guest");
  return labels.join(" · ");
}

function emptyPrompt(filterId) {
  if (filterId === "followers") return "No followers yet. Dancers can follow a recurring event when they RSVP.";
  if (filterId === "regulars") return "No regulars who aren't following yet.";
  if (filterId === "past") return "Everyone past has already RSVP'd, or there's no history yet.";
  return "No contacts to reach yet.";
}

export default function NotifyTab({
  attendees,
  updateSubscribers,
  repeatWithoutFavourite = [],
  eventId,
  occurrenceDate,
  tokenParam,
  rsvpCount = 0,
  demoMode = false,
  demoBroadcasts = null,
  onDemoBroadcast,
}) {
  const contacts = useMemo(
    () =>
      buildContacts({
        attendees,
        updateSubscribers,
        regulars: repeatWithoutFavourite,
      }),
    [attendees, updateSubscribers, repeatWithoutFavourite],
  );

  const counts = useMemo(() => {
    const next = { all: contacts.length, regulars: 0, past: 0, followers: 0 };
    for (const c of contacts) {
      if (c.segments.includes("regulars")) next.regulars += 1;
      if (c.segments.includes("past")) next.past += 1;
      if (c.segments.includes("followers")) next.followers += 1;
    }
    return next;
  }, [contacts]);

  const visibleFilters = useMemo(
    () => FILTERS.filter((f) => f.id === "all" || counts[f.id] > 0),
    [counts],
  );

  const [filter, setFilter] = useState("all");
  const activeFilter = visibleFilters.some((f) => f.id === filter) ? filter : "all";

  const filtered = useMemo(() => {
    if (activeFilter === "all") return contacts;
    return contacts.filter((c) => c.segments.includes(activeFilter));
  }, [contacts, activeFilter]);

  return (
    <div className="max-w-page mx-auto px-4 mt-6 space-y-10 pb-8">
      <BroadcastPanel
        eventId={eventId}
        occurrenceDate={occurrenceDate}
        tokenParam={tokenParam}
        rsvpCount={rsvpCount}
        demoMode={demoMode}
        demoBroadcasts={demoBroadcasts}
        onDemoBroadcast={onDemoBroadcast}
      />

      <section className="space-y-4" aria-labelledby="reach-out-contacts-title">
        <header className="space-y-1">
          <h2 id="reach-out-contacts-title" className="font-ui font-semibold text-[18px] sm:text-[20px] leading-tight">
            Re-engage & Invite Dancers
          </h2>
          <p className="font-ui font-medium text-[14px] text-ink/70 leading-snug">
            Reach out to regulars, followers, and past guests who haven&apos;t signed up yet.
          </p>
        </header>

        {counts.all === 0 ? (
          <p className="font-ui font-medium text-[14px] text-ink/60" data-testid="reach-out-contacts-empty">
            No contacts to reach yet.
          </p>
        ) : (
          <>
            <div
              className="flex gap-2 flex-wrap"
              role="group"
              aria-label="Contact filters"
              data-testid="reach-out-filter-chips"
            >
              {visibleFilters.map((f) => (
                <ReadablePillToggle
                  key={f.id}
                  active={activeFilter === f.id}
                  onClick={() => setFilter(f.id)}
                  aria-pressed={activeFilter === f.id}
                  data-testid={`reach-out-filter-${f.id}`}
                >
                  {f.label}
                  <span className="tabular-nums text-ink/55">· {counts[f.id]}</span>
                </ReadablePillToggle>
              ))}
            </div>

            <div className="space-y-2" data-testid="notify-list">
              {/* Keep legacy test hooks for demo tour / followers list */}
              <div className="sr-only" data-testid="update-notify-list" aria-hidden>
                {counts.followers}
              </div>
              <div className="sr-only" data-testid="repeat-without-favourite-list" aria-hidden>
                {counts.regulars}
              </div>

              {filtered.length === 0 ? (
                <p className="font-ui font-medium text-[14px] text-ink/60">{emptyPrompt(activeFilter)}</p>
              ) : (
                filtered.map((c) => (
                  <NotifyRow
                    key={c.dancer_id}
                    name={c.name}
                    meta={[contactStatus(c), contactMeta(c)].filter(Boolean).join(" · ")}
                    whatsappUrl={c.whatsapp_url}
                    testId={
                      c.segments.includes("followers")
                        ? `update-subscriber-${c.dancer_id}`
                        : c.segments.includes("regulars")
                          ? `repeat-no-fav-${c.dancer_id}`
                          : `past-attendee-${c.dancer_id}`
                    }
                    pingTestId={
                      c.segments.includes("followers")
                        ? `ping-update-${c.dancer_id}-btn`
                        : c.segments.includes("regulars")
                          ? `ping-repeat-${c.dancer_id}-btn`
                          : `ping-${c.dancer_id}-btn`
                    }
                  />
                ))
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
